import { Box, Divider, Stack } from '@mui/material';
import { CreatableTags, DatePicker, Select, TextField } from '@travelity/form';
import { convertEnumToList, emailRefEx } from '@travelity/utils';
import React, { useMemo } from 'react';
import Button from '@mui/material/Button';
import { Country, Nationality } from '@travelity/api';
import { useMediaDevice } from '@travelity/ui';
import { useTranslation } from 'react-i18next';
import { ReactComponent as Attachments } from '../assets/attachments.svg';
import { ReactComponent as ProfilePic } from '../assets/profile-pic.svg';

export interface PersonalInfoProps {
    onSkip: () => void;
}

export const PersonalInfo: React.FC<PersonalInfoProps> = props => {
    const { onSkip } = props;
    const { t } = useTranslation();
    const countries = useMemo(() => convertEnumToList<Country>(Country), []);
    const nationalities = useMemo(
        () => convertEnumToList<Nationality>(Nationality),
        []
    );
    const { isMobile } = useMediaDevice();

    return (
        <Stack direction="row" gap={5} sx={{ width: { xs: 1, sm: 'auto' } }}>
            {!isMobile && (
                <Stack sx={{ width: '244px' }}>
                    <ProfilePic />
                </Stack>
            )}
            <Stack sx={{ width: { xs: 1, sm: '404px' }, p: 1.5 }} gap={2}>
                <Box>
                    <Divider>{t('Contact Details')}</Divider>
                </Box>
                <CreatableTags
                    name="emails"
                    leaveLast
                    regEx={emailRefEx}
                    inputProps={{
                        sx: {
                            '&& .MuiInputBase-root': {
                                paddingRight: '10px',
                            },
                        },
                        label: 'Email',
                        placeholder: 'Type email and press enter',
                    }}
                />
                <CreatableTags
                    name="numbers"
                    regEx={/^\+?\d+ \d+$/}
                    inputProps={{
                        sx: {
                            '&& .MuiInputBase-root': {
                                paddingRight: '10px',
                            },
                        },
                        label: 'Phone Numbers',
                        placeholder: 'Type number and press enter',
                    }}
                />
                <Divider sx={{ mt: 1.5 }}>{t('Personal Information')}</Divider>
                <DatePicker
                    label={t('Date of Birth', { ns: 'common' })}
                    name="birthDate"
                    TextFieldProps={{
                        helperText: '',
                        placeholder: 'Select date of birth',
                    }}
                />
                <TextField
                    label={t('Place of Birth', { ns: 'common' })}
                    name="birthPlace"
                    placeholder={t('Type Place of Birth', { ns: 'common' })}
                />
                <Select
                    label={t('Nationality', { ns: 'common' })}
                    name="nationality"
                    placeholder={t('Select Nationality', { ns: 'common' })}
                    options={nationalities}
                />
                <Divider sx={{ mt: 1.5 }}>
                    {t('Identification Document')}
                </Divider>
                <TextField
                    label={t('Passport №', { ns: 'common' })}
                    name="passportN"
                    placeholder="AA0000000"
                />
                <TextField
                    label={t('Issued by', { ns: 'common' })}
                    name="issuedBy"
                    placeholder="000"
                />
                <Stack direction="row" gap={1}>
                    <DatePicker
                        label={t('Issued at', { ns: 'common' })}
                        name="issuedAt"
                        TextFieldProps={{
                            helperText: '',
                            placeholder: 'Select date',
                        }}
                    />
                    <DatePicker
                        label={t('Expires at', { ns: 'common' })}
                        name="expiresAt"
                        TextFieldProps={{
                            helperText: '',
                            placeholder: 'Select date',
                        }}
                    />
                </Stack>
                <Select
                    label={t('Citizenship', { ns: 'common' })}
                    name="citizenship"
                    placeholder={t('Select Citizenship', { ns: 'common' })}
                    options={countries}
                />
                <Stack direction="row" spacing={1}>
                    <Button
                        color="neutral"
                        variant="contained"
                        onClick={onSkip}
                        sx={{
                            px: 10,
                            py: 1,
                            '&&&': { pointerEvents: 'initial' },
                        }}
                    >
                        Skip
                    </Button>
                    <Button
                        color="secondary"
                        variant="contained"
                        type="submit"
                        sx={{
                            px: 10,
                            py: 1,
                            '&&&': { pointerEvents: 'initial' },
                        }}
                    >
                        Continue
                    </Button>
                </Stack>
            </Stack>
            {!isMobile && (
                <Box sx={{ p: 1, width: '260px' }}>
                    <Divider>{t('Attachments')}</Divider>
                    <Attachments />
                </Box>
            )}
        </Stack>
    );
};
